import React, { useEffect, useState, useContext, useRef } from 'react';
import { Context } from '../../../App';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import InputAdornment from '@mui/material/InputAdornment';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MuiSnackbar from '../../common/MuiSnackbar';
import CircularProgress from '@mui/material/CircularProgress';
import SeoHelmet from '../../common/SeoHelmet';
import CalculateButton from '../../common/calculateButton';
import SaveButton from '../../common/saveButton';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import Sidebar from '../../sidebar/Sidebar';
import DecisionModal from '../../modals/DecisionModal';
import PricingInfoModal from '../../modals/PricingInfoModal';
import TerzaghiResult from './TerzaghiResult';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PureTerzaghiSketch from './InteractiveSketch';
import { useCalculationContext, CalculationContextProvider } from '../../context/calculationContext';
import useLoadSavedCalculation from '../useLoadSavedCalculation';
import { svgContainerToPngBase64 } from '../../../helpers/svgContainerToPngBase64';

const Terzaghi = () => {
  const { user } = useContext(Context);

  const {
    MAX_NAME_LENGTH,
    isLoadingCalculate,
    isLoadingLoadAll,
    isLoadingLoad,
    isLoadingSave,
    result,
    calculations,
    lastData,
    showOutput,
    setShowOutput,
    calculateButtonDisabled,
    setCalculateButtonDisabled,
    saveButtonDisabled,
    setSaveButtonDisabled,
    errorMessage,
    displaySidebar,
    setDisplaySidebar,
    decisionModalIsOpen,
    decision,
    setDecision,
    handleSidebarDisplay,
    setDecisionModalIsOpenToFalse,
    showMuiSnack,
    open,
    setOpen,
    alertMessage,
    severity,
    onChangeWarningForPositiveDecimals,
    calculate,
    loadOneCalculation,
    loadAllCalculations,
    update,
    checkCalculationNameforSaving,
    checkCalculationNameforSavingAs,
    deleteCalculations,
    calculationCountReached,
    saveCountReached,
  } = useCalculationContext();

  //SET STATES FOR INPUT VARIABLES
  const [name, setName] = useState('');
  const [cohesion, setCohesion] = useState('');
  const [phi, setPhi] = useState('');
  const [df, setDf] = useState('');
  const [fndWidth, setFndWidth] = useState('');
  const [gamma, setGamma] = useState('');
  const [fndType, setFndType] = useState(0);
  const [gwt, setGwt] = useState('');
  const [FsSt, setFsSt] = useState('');
  const [FsSs, setFsSs] = useState('');
  const [FSt, setFSt] = useState('');
  const [FSs, setFSs] = useState('');

  //PDF RELATED STATES
  const [isExporting, setIsExporting] = useState(false);
  const [showPdfWarningModal, setShowPdfWarningModal] = useState(false);
  const sketchContainerRef = useRef(null);

  // SET THE LOADED INPUTS
  const setInputs = (inputs) => {
    setName(inputs.name);
    setCohesion(inputs.cohesion);
    setPhi(inputs.phi);
    setDf(inputs.df);
    setFndWidth(inputs.fndWidth);
    setGamma(inputs.gamma);
    setFndType(inputs.fndType);
    setGwt(inputs.gwt);
    setFsSt(inputs.FsSt);
    setFsSs(inputs.FsSs);
    setFSt(inputs.FSt);
    setFSs(inputs.FSs);
  };

  //INPUTS FOR CALCULATION
  const calculationInput = {
    name,
    cohesion,
    phi,
    df,
    fndWidth,
    gamma,
    fndType,
    gwt,
    FsSt,
    FsSs,
    FSt,
    FSs,
  };

  // MANAGE SAVE & CALCULATE BUTTONS DISABLED/ENABLED STATES
  useEffect(() => {
    setCalculateButtonDisabled(false);
    setSaveButtonDisabled(false);

    if (
      (calculationInput.name || calculationInput.name === 0) &&
      (calculationInput.cohesion || calculationInput.cohesion === 0) &&
      (calculationInput.phi || calculationInput.phi === 0) &&
      (calculationInput.df || calculationInput.df === 0) &&
      (calculationInput.fndWidth || calculationInput.fndWidth === 0) &&
      (calculationInput.gamma || calculationInput.gamma === 0) &&
      (calculationInput.fndType || calculationInput.fndType === 0) &&
      (calculationInput.gwt || calculationInput.gwt === 0) &&
      (calculationInput.FsSt || calculationInput.FsSt === 0) &&
      (calculationInput.FsSs || calculationInput.FsSs === 0) &&
      (calculationInput.FSt || calculationInput.FSt === 0) &&
      (calculationInput.FSs || calculationInput.FSs === 0)
    ) {
      setCalculateButtonDisabled(false);
      setSaveButtonDisabled(false);
    } else {
      setCalculateButtonDisabled(true);
      setSaveButtonDisabled(true);
    }
  }, [
    setCalculateButtonDisabled,
    setSaveButtonDisabled,
    calculationInput.name,
    calculationInput.cohesion,
    calculationInput.phi,
    calculationInput.df,
    calculationInput.fndWidth,
    calculationInput.gamma,
    calculationInput.fndType,
    calculationInput.gwt,
    calculationInput.FsSt,
    calculationInput.FsSs,
    calculationInput.FSt,
    calculationInput.FSs,
  ]);

  // Do not render output if any input parameter except name changes
  useEffect(() => {
    setShowOutput(false);
  }, [
    setShowOutput,
    calculationInput.cohesion,
    calculationInput.phi,
    calculationInput.df,
    calculationInput.fndWidth,
    calculationInput.gamma,
    calculationInput.fndType,
    calculationInput.gwt,
    calculationInput.FsSt,
    calculationInput.FsSs,
    calculationInput.FSt,
    calculationInput.FSs,
  ]);

  //CLEAR INPUTS AND CLEAR OUTPUT
  const createNewCalculation = () => {
    setName('');
    setCohesion('');
    setPhi('');
    setDf('');
    setFndWidth('');
    setGamma('');
    setFndType('');
    setGwt('');
    setFsSt('');
    setFsSs('');
    setFSt('');
    setFSs('');
    setShowOutput(false);
  };

  //API URLS
  const URL = {
    CALCULATE: '/api/v1/calculations/terzaghi',
    LOADONE: `/api/v1/calculations/terzaghi/loadOne/`,
    LOADALL: '/api/v1/calculations/terzaghi/loadAll',
    UPDATE: `/api/v1/calculations/terzaghi/update/`,
    SAVE: '/api/v1/calculations/terzaghi/save',
    CHECKUNIQUENAME: `/api/v1/calculations/terzaghi/checkUniqueName/`,
    DELETE: '/api/v1/calculations/terzaghi/delete',
  };
  useLoadSavedCalculation({ URL, loadOneCalculation, calculate, setInputs, showMuiSnack });

  const handleChangeName = (e) => {
    if (e.target.value.length >= MAX_NAME_LENGTH) {
      showMuiSnack(`Calculation name can be max ${MAX_NAME_LENGTH} characters.`, 'warning');
    }
    setName(e.target.value);
  };

  const handleChangePhi = (e) => {
    const value = e.target.value;

    // Reject non-numeric characters except empty string, dot, or one decimal point
    const numericRegex = /^(\d+(\.\d*)?)?$/;
    if (!numericRegex.test(value)) {
      showMuiSnack('Only numeric values are allowed for φ.', 'warning');
      return;
    }

    // Allow empty (to enable user to clear field)
    if (value === '') {
      setPhi(value);
      return;
    }

    const num = parseFloat(value);

    // Range check
    if (num < 0 || num > 50) {
      showMuiSnack('φ must be between 0° and 50° (inclusive).', 'warning');
      return;
    }

    setPhi(value);
  };

  //TABS
  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  //HANDLE PDF
  const handleExportPdf = async () => {
    if (isExporting) return;
    if (user.role === 'basic' || user.role === 'standard') {
      setShowPdfWarningModal(true);
      return;
    }
    setIsExporting(true);
    try {
      const [sketchPngBase64, uvtPngBase64] = await Promise.all([
        svgContainerToPngBase64(sketchContainerRef.current, {
          scale: 2, // 1)Low resolution, 2)High Resolution
        }),
      ]);

      const payload = {
        ...calculationInput,
        sketchPngBase64,
        uvtPngBase64,
      };

      const res = await fetch('/api/v1/pdf/terzaghi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('PDF generation failed');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'geocalc-terzaghi-report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      // optionally show a snackbar
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <SeoHelmet
        title="GeoCalc | Bearing Capacity of Shallow Foundations — Terzaghi (1943)"
        description="Compute ultimate bearing capacity and allowable pressure for shallow foundations using Terzaghi (1943): strip, square, circular; groundwater and safety-factor options."
        url="https://www.geocalc.app/calculations/terzaghi"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="tool"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'Bearing Capacity | Terzaghi (1943)', url: 'https://www.geocalc.app/calculations/terzaghi' },
        ]}
      />

      <Header />
      <div className="container">
        <MuiSnackbar open={open} setOpen={setOpen} severity={severity} alertMessage={alertMessage} autoHideDuration={3000} />
        {displaySidebar ? (
          <Sidebar
            URL={URL}
            loadAllCalculations={loadAllCalculations}
            calculations={calculations}
            loadOneCalculation={loadOneCalculation}
            calculate={calculate}
            setInputs={setInputs}
            deleteCalculations={deleteCalculations}
            createNewCalculation={createNewCalculation}
            isLoading={isLoadingLoadAll}
            handleSidebarDisplay={handleSidebarDisplay}
            displaySidebar={displaySidebar}
            setDisplaySidebar={setDisplaySidebar}
          />
        ) : (
          <MenuOpenIcon
            color="primary"
            fontSize="large"
            className="sidebar-open-icon"
            alt="side bar menu icon"
            label="Show my calculations"
            onClick={handleSidebarDisplay}
          />
        )}
        <DecisionModal
          URL={URL}
          data={calculationInput}
          decisionModalIsOpen={decisionModalIsOpen}
          setDecisionModalIsOpenToFalse={setDecisionModalIsOpenToFalse}
          decision={decision}
          setDecision={setDecision}
          checkCalculationNameforSavingAs={checkCalculationNameforSavingAs}
          name={name}
          setName={setName}
          errorMessage={errorMessage}
          update={update}
          saveButtonDisabled={saveButtonDisabled}
        />
        <PricingInfoModal open={showPdfWarningModal} setOpen={setShowPdfWarningModal} role={user.role} />
        <div className="right-calculation">
          <div className="terzaghiContainer">
            <h1>Bearing Capacity of Shallow Foundations - Terzaghi (1943)</h1>
            <div className="terzagiTabContainer">
              <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons allowScrollButtonsMobile>
                <Tab label="Soil Properties" sx={{ textTransform: 'none' }} />
                <Tab label="Foundation Geometry" sx={{ textTransform: 'none' }} />
                <Tab label="Loads" sx={{ textTransform: 'none' }} />
                <Tab label="Factor of Safety" sx={{ textTransform: 'none' }} />
              </Tabs>
            </div>
            {value === 0 && (
              <div className="terzaghiInputs">
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label="Name"
                  variant="outlined"
                  inputProps={{
                    maxLength: `${MAX_NAME_LENGTH}`,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title={`Your calculation name (Max. ${MAX_NAME_LENGTH} characters)`}
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={name}
                  onChange={handleChangeName}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label="c (kPa)"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Cohesion" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={cohesion}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Cohesion', setCohesion)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  label={
                    <span key={'phi'} className="subscriptMargin">
                      &phi; (<sup>o</sup>)
                    </span>
                  }
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Internal friction angle" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={phi}
                  onChange={handleChangePhi}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label={
                    <span key={'gamma'} className="subscriptMargin">
                      &gamma; (kN/m<sup>3</sup>)
                    </span>
                  }
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Unit weight of soil" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={gamma}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Gamma', setGamma)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  label={'GWT (m)'}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title="Ground water level measured from ground surface"
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={gwt}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Gwt', setGwt)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
              </div>
            )}
            {value === 1 && (
              <div className="terzaghiInputs">
                <TextField
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  label={
                    <span key={'df'}>
                      D<sub>f</sub> (m)
                    </span>
                  }
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Depth of foundation" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={df}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Df', setDf)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  label={'B (m)'}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Foundation width" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={fndWidth}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'B', setFndWidth)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />

                <TextField
                  style={{ minWidth: 120, maxWidth: 120 }}
                  label={'Fnd Type'}
                  size="small"
                  select
                  value={fndType}
                  onChange={(e) => setFndType(e.target.value)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                >
                  <MenuItem value="0" disabled={true}>
                    Select Foundation Type
                  </MenuItem>
                  <MenuItem value="1">Strip</MenuItem>
                  <MenuItem value="2">Circular</MenuItem>
                  <MenuItem value="3">Square</MenuItem>
                </TextField>
              </div>
            )}
            {value === 2 && (
              <div className="terzaghiInputs">
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label={'FSt(kPa)'}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Static load" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={FSt}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'FSt', setFSt)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label={'FSs(kPa)'}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Seismic load" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={FSs}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'FSs', setFSs)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
              </div>
            )}
            {value === 3 && (
              <div className="terzaghiInputs">
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label={'FsSt'}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Factor of safety (Static)" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={FsSt}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'FsSt', setFsSt)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label={'FsSs'}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Factor of safety (Seismic)" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={FsSs}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'FsSs', setFsSs)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
              </div>
            )}

            <PureTerzaghiSketch
              cohesion={cohesion}
              phi={phi}
              df={df}
              fndWidth={fndWidth}
              gamma={gamma}
              fndType={fndType}
              gwt={gwt}
              FsSt={FsSt}
              FsSs={FsSs}
              FSt={FSt}
              FSs={FSs}
              calculateButtonDisabled={calculateButtonDisabled}
            />
            {/* For PDF Exports */}
            <div
              ref={sketchContainerRef}
              style={{
                position: 'absolute',
                left: -99999,
                top: -99999,
                width: 800,
                height: 350,
                pointerEvents: 'none',
                overflow: 'hidden',
              }}
            >
              <PureTerzaghiSketch
                cohesion={cohesion}
                phi={phi}
                df={df}
                fndWidth={fndWidth}
                gamma={gamma}
                fndType={fndType}
                gwt={gwt}
                FsSt={FsSt}
                FsSs={FsSs}
                FSt={FSt}
                FSs={FSs}
                calculateButtonDisabled={calculateButtonDisabled}
                variant={'pdf-light'}
              />
            </div>
            <div className="buttonContainer terzaghiSaveCalculateButtonContainer">
              <CalculateButton
                calculationCountReached={calculationCountReached}
                calculate={calculate}
                calculationInput={calculationInput}
                URL={URL}
                calculateButtonDisabled={calculateButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
              />
              <Button
                variant="contained"
                startIcon={!isExporting ? <PictureAsPdfIcon /> : null}
                onClick={handleExportPdf}
                disabled={!showOutput || isExporting}
                style={{ width: '200px' }}
              >
                {isExporting ? (
                  <>
                    <CircularProgress size={18} style={{ marginRight: 8 }} />
                    Creating PDF…
                  </>
                ) : (
                  'Create PDF'
                )}
              </Button>
              <SaveButton
                saveCountReached={saveCountReached}
                checkCalculationNameforSaving={checkCalculationNameforSaving}
                calculationInput={calculationInput}
                URL={URL}
                saveButtonDisabled={saveButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
              />
            </div>
          </div>
          {showOutput && <TerzaghiResult result={result} />}
          {isLoadingLoad && <CircularProgress size={30} />}

          {isLoadingCalculate && (
            <div className="centerLoading">
              <CircularProgress />
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

const TerzaghiWithProvider = () => {
  return (
    <CalculationContextProvider>
      <Terzaghi />
    </CalculationContextProvider>
  );
};
export default TerzaghiWithProvider;
